// src/pages/AffiliateStats.jsx
import React, { useEffect, useState, useMemo } from "react";
import axios from "../services/api";
import { affiliateExternalApi } from "../services/externalPlatformApi";
import { format } from "date-fns";

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(Number(n || 0));

export default function AffiliateStats() {
  const [stats, setStats] = useState([]);
  const [externalStats, setExternalStats] = useState(null);
  const [activeTab, setActiveTab] = useState("inHouse");
  const [loading, setLoading] = useState(true);
  const [externalLoading, setExternalLoading] = useState(true);
  const [externalError, setExternalError] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get("/api/affiliate/stats");
        // show only last 6 months (as before)
        setStats((res.data || []).slice(0, 6));
      } catch {
        setError("Failed to load stats.");
      } finally {
        setLoading(false);
      }
    })();

    (async () => {
      try {
        const data = await affiliateExternalApi.getStats();
        setExternalStats(data);
      } catch {
        setExternalError("External earnings are waiting for the new backend endpoint.");
      } finally {
        setExternalLoading(false);
      }
    })();
  }, []);

  const totals = useMemo(() => {
    const sum = (key) => (stats || []).reduce((a, b) => a + Number(b?.[key] || 0), 0);
    return {
      totalTickets: sum("totalTickets"),
      standardTickets: sum("standardTickets"),
      vipTickets: sum("vipTickets"),
      commissionEarned: sum("commissionEarned"),
      bonusEarned: sum("bonusEarned"),
    };
  }, [stats]);

  if (loading) {
    return (
      <div className="page space-y-6">
        <section className="card animate-pulse">
          <div className="h-6 w-40 bg-white/5 rounded" />
          <div className="mt-4 grid gap-3 md:grid-cols-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="h-4 w-24 bg-white/5 rounded" />
                <div className="mt-2 h-6 w-16 bg-white/5 rounded" />
              </div>
            ))}
          </div>
        </section>
        <section className="grid gap-4 md:grid-cols-2">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="card animate-pulse">
              <div className="h-5 w-48 bg-white/5 rounded" />
              <div className="mt-3 h-4 w-full bg-white/5 rounded" />
              <div className="mt-2 h-4 w-2/3 bg-white/5 rounded" />
              <div className="mt-4 h-3 w-full bg-white/5 rounded" />
            </div>
          ))}
        </section>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <section className="card bg-red-500/10 border-red-500/20 text-red-100">
          {error}
        </section>
      </div>
    );
  }

  return (
    <div className="page space-y-6">
      {/* HEADER + SUMMARY */}
      <section className="card">
        <div className="md:flex md:items-end md:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl md:text-4xl">Affiliate Sales</h1>
            <p className="mt-1 text-slate-300">
              Last {stats.length || 0} month{stats.length === 1 ? "" : "s"} performance overview.
            </p>
          </div>
          {stats.length > 0 && (
            <span className="badge bg-brand-600/20 text-slate-100">
              {totals.totalTickets} ticket{totals.totalTickets === 1 ? "" : "s"} total
            </span>
          )}
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            className={activeTab === "inHouse" ? "btn-primary" : "btn-secondary"}
            onClick={() => setActiveTab("inHouse")}
          >
            In-house tickets
          </button>
          <button
            type="button"
            className={activeTab === "external" ? "btn-primary" : "btn-secondary"}
            onClick={() => setActiveTab("external")}
          >
            External earnings
          </button>
        </div>

        {/* KPI STRIP */}
        {activeTab === "inHouse" ? (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KPI label="Commission (bonus inclusive)" value={fmtGBP(totals.commissionEarned)} />
            <KPI label="Bonus (sum)" value={fmtGBP(totals.bonusEarned)} />
            <KPI label="Standard Tickets" value={totals.standardTickets} />
            <KPI label="VIP Tickets" value={totals.vipTickets} />
          </div>
        ) : (
          <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KPI label="Confirmed external" value={fmtGBP(externalStats?.confirmedEarnings)} />
            <KPI label="Pending external" value={fmtGBP(externalStats?.pendingEarnings)} />
            <KPI label="External sales" value={externalStats?.totalSales || 0} />
            <KPI label="Affiliate code" value={externalStats?.affiliateCode || "Pending"} />
          </div>
        )}
      </section>

      {/* MONTHLY BREAKDOWN */}
      {activeTab === "inHouse" && (
        stats.length === 0 ? (
          <section className="card text-slate-300">No stats available yet.</section>
        ) : (
          <section className="grid gap-4 md:grid-cols-2">
            {stats.map((m) => (
              <MonthCard key={m.month} data={m} />
            ))}
          </section>
        )
      )}

      {activeTab === "external" && (
        <ExternalEarnings
          stats={externalStats}
          loading={externalLoading}
          error={externalError}
        />
      )}
    </div>
  );
}

/* ---------------- Bits ---------------- */

function KPI({ label, value }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-xl font-semibold text-white">{value}</div>
    </div>
  );
}

function MonthCard({ data }) {
  const {
    month,
    totalTickets = 0,
    standardTickets = 0,
    vipTickets = 0,
    commissionEarned = 0,
    bonusEarned = 0,
    ticketsTowardGoal = 0,
    ticketsRemainingToGoal = 0,
  } = data || {};

  // Month label: safely parse "YYYY-MM"
  let label = "—";
  try {
    const [y, m] = String(month || "").split("-").map(Number);
    label = isFinite(y) && isFinite(m) ? format(new Date(y, m - 1), "MMMM yyyy") : "—";
  } catch {
    label = "—";
  }

  const commissionRate = totalTickets >= 14 ? "20%" : "10%";
  const progress = Math.max(0, Math.min(100, (Number(ticketsTowardGoal || 0) / 14) * 100));

  return (
    <article className="card">
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-xl font-semibold text-white">{label}</h3>
        <div className="flex flex-wrap gap-2">
          {bonusEarned > 0 && (
            <span className="badge bg-emerald-500/20 text-emerald-200">£200 bonus</span>
          )}
          <span className="badge bg-brand-600/20 text-slate-100">{commissionRate}</span>
        </div>
      </div>

      <div className="mt-3 grid gap-3 text-sm text-slate-300 sm:grid-cols-2">
        <Metric label="Standard Tickets" value={standardTickets} />
        <Metric label="VIP Tickets" value={vipTickets} />
        <Metric label="Total Tickets" value={totalTickets} />
        <Metric label="Commission Earned" value={fmtGBP(commissionEarned)} />
      </div>

      {/* Progress */}
      <div className="mt-4">
        <div className="flex items-center justify-between text-xs text-slate-400">
          <span>Progress to 14-ticket goal</span>
          <span>
            {ticketsTowardGoal}/14 ({Math.round(progress)}%)
          </span>
        </div>
        <div
          className="mt-1 h-2 w-full overflow-hidden rounded-full bg-white/10"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={14}
          aria-valuenow={ticketsTowardGoal}
        >
          <div
            className="h-full bg-gradient-to-r from-brand-600 to-glow transition-[width]"
            style={{ width: `${progress}%` }}
          />
        </div>

        {ticketsRemainingToGoal > 0 ? (
          <p className="mt-2 text-xs text-slate-400">
            {ticketsRemainingToGoal} more to hit the £200 bonus this month.
          </p>
        ) : (
          <p className="mt-2 text-xs text-emerald-300">Bonus target hit — nice!</p>
        )}
      </div>
    </article>
  );
}

function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] px-3 py-2">
      <div className="text-[11px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-0.5 font-medium text-white">{value}</div>
    </div>
  );
}

function ExternalEarnings({ stats, loading, error }) {
  if (loading) {
    return <section className="card text-slate-300">Loading external earnings...</section>;
  }

  if (error) {
    return (
      <section className="card border-amber-500/20 bg-amber-500/10 text-amber-100">
        {error}
      </section>
    );
  }

  const monthly = Array.isArray(stats?.monthly) ? stats.monthly : [];
  const sales = Array.isArray(stats?.sales) ? stats.sales : [];

  return (
    <div className="space-y-6">
      {monthly.length === 0 ? (
        <section className="card text-slate-300">No external earnings yet.</section>
      ) : (
        <section className="grid gap-4 md:grid-cols-2">
          {monthly.map((month) => (
            <article key={month.month} className="card">
              <h3 className="text-xl font-semibold text-white">{month.month}</h3>
              <div className="mt-3 grid gap-3 text-sm sm:grid-cols-2">
                <Metric label="Sales" value={month.sales || 0} />
                <Metric label="Confirmed" value={fmtGBP(month.confirmed)} />
                <Metric label="Pending" value={fmtGBP(month.pending)} />
                <Metric label="Cancelled" value={fmtGBP(month.cancelled)} />
              </div>
            </article>
          ))}
        </section>
      )}

      {sales.length > 0 && (
        <section className="card overflow-hidden">
          <h2 className="text-xl font-semibold">Recent external sales</h2>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-white/10 text-xs uppercase tracking-wider text-slate-400">
                <tr>
                  <th className="py-3 pr-4">Date</th>
                  <th className="py-3 pr-4">Partner</th>
                  <th className="py-3 pr-4">Sale</th>
                  <th className="py-3 pr-4">Your payout</th>
                  <th className="py-3 pr-4">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {sales.map((sale) => (
                  <tr key={sale.id}>
                    <td className="py-3 pr-4 text-slate-300">
                      {sale.created_at ? new Date(sale.created_at).toLocaleDateString() : "TBC"}
                    </td>
                    <td className="py-3 pr-4 text-slate-300">{sale.partner_name || "Awin"}</td>
                    <td className="py-3 pr-4 text-slate-300">{fmtGBP(sale.sale_amount)}</td>
                    <td className="py-3 pr-4 text-white">{fmtGBP(sale.affiliate_payout)}</td>
                    <td className="py-3 pr-4">
                      <span className="badge">{sale.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
